import { motion } from 'framer-motion';
import { stagger, fadeUp, viewport } from '../../utils/animations';
import {
  HiCodeBracket, HiServer, HiDevicePhoneMobile, HiRocketLaunch,
} from 'react-icons/hi2';

const SERVICES = [
  {
    icon:  HiCodeBracket,
    title: 'Front-End Engineering',
    desc:  'Pixel-perfect, performant UIs built with React, Next.js & Angular. Design systems, component libraries, and micro-frontends that scale.',
    tech:  ['React', 'Next.js', 'Angular', 'TypeScript', 'Tailwind','Bootstrap', 'MUI', 'Shadcn/UI','Tamagui', 'NativeWind','GitLab', 'Figma', 'CI/CD','Redux/RTK', 'Zustand','+ more'],
    color: '#6366f1',
    num:   '01',
    bg:    'from-indigo-600/10',
  },
  {
    icon:  HiServer,
    title: 'Back-End & APIs',
    desc:  'Scalable REST & GraphQL APIs, database design with MongoDB & PostgreSQL, secure auth flows, and clean architecture.',
    tech:  ['Node.js', 'Express', 'MongoDB', 'Prisma', 'Firebase', 'AWS', 'Docker', 'GitHub Actions','CI/CD', 'GitLab', '+ more'],
    color: '#8b5cf6',
    num:   '02',
    bg:    'from-violet-600/10',
  },
  {
    icon:  HiDevicePhoneMobile,
    title: 'Mobile Development',
    desc:  'Cross-platform iOS & Android apps with React Native and Expo — sharing code with your web stack for maximum efficiency.',
    tech:  ['React Native', 'Expo','Tamagui', 'NativeWind', 'GitLab', 'Figma', 'CI/CD', 'Redux/RTK', 'Zustand','Firebase', '+ more'],
    color: '#06b6d4',
    num:   '03',
    bg:    'from-cyan-600/10',
  },
  {
    icon:  HiRocketLaunch,
    title: 'Performance & DevOps',
    desc:  'Core Web Vitals optimization, CI/CD pipelines, containerization and cloud deployments on AWS & Docker for production-grade reliability.',
    tech:  ['AWS EC2', 'Docker', 'GitHub Actions', 'Nginx', 'CI/CD', 'GitLab', '+ more'],
    color: '#10b981',
    num:   '04',
    bg:    'from-emerald-600/10',
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 20% 60%, rgba(99,102,241,0.04) 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">

        <motion.div variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport} className="mb-14">
          <motion.div variants={fadeUp} className="section-label">
            <span className="font-mono text-xs text-indigo-400 font-semibold uppercase tracking-[0.18em]">03 / Services</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight max-w-xl">
            What I <span className="gradient-text">bring to the table</span>
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5"
        >
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                variants={fadeUp}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                className="group relative glass rounded-3xl border border-white/[0.06] overflow-hidden cursor-default"
              >
                <div
                  className="absolute -bottom-4 -right-2 font-display font-extrabold text-[90px] leading-none select-none pointer-events-none opacity-[0.04] group-hover:opacity-[0.07] transition-opacity duration-500"
                  style={{ color: s.color }}
                >
                  {s.num}
                </div>

                <div
                  className={`absolute inset-0 bg-gradient-to-b ${s.bg} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                />

                <div className="relative p-6 sm:p-7 flex flex-col h-full">
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                    className="w-12 h-12 flex items-center justify-center rounded-2xl mb-5 flex-shrink-0"
                    style={{ background: `${s.color}18`, border: `1px solid ${s.color}35` }}
                  >
                    <Icon className="w-5.5 h-5.5" style={{ color: s.color }} />
                  </motion.div>

                  <h3 className="font-display font-bold text-lg text-white mb-2.5 leading-tight">{s.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed mb-5 flex-1">{s.desc}</p>

                  <div
                    className="h-px mb-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `linear-gradient(90deg, ${s.color}, transparent)` }}
                  />

                  <div className="flex flex-wrap gap-1.5">
                    {s.tech.map(t => (
                      <span
                        key={t}
                        className="font-mono text-[10px] px-2 py-0.5 rounded-lg text-zinc-700 bg-white/[0.03] border border-white/[0.05]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `linear-gradient(90deg, transparent, ${s.color}, transparent)` }}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}